import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { useOrganization } from '../context/OrganizationContext';

/**
 * SINGLE SOURCE OF TRUTH — channels that can MESSAGE today.
 *
 * A token-sane (connected) channelAccount is NOT enough for a channel to be
 * "operational": the channel must ALSO be messaging-capable. TikTok is intentionally
 * EXCLUDED — its Business Messaging API is not enabled yet (DSPR under review), so a
 * TikTok channelAccount can have a healthy token and still NOT be able to message.
 *
 * When TikTok goes live, add 'tiktok' here (ONE line) and every status surface lights
 * up on its own — no condicionales to chase across files.
 */
export const MESSAGING_CAPABLE = ['whatsapp', 'instagram', 'messenger', 'tiktok'];

/**
 * The one definition of "channel X is OPERATIONAL for this owner":
 *   (a) ≥1 channelAccount with canal==X and estado=='conectado' (token sane, excludes
 *       error_token), AND
 *   (b) X ∈ MESSAGING_CAPABLE (can message today).
 *
 * Subscribes (live) to channelAccounts for each requested channel. Channels NOT in
 * MESSAGING_CAPABLE short-circuit to false WITHOUT opening a listener.
 *
 * ⚠️ CORRECCIÓN (17-sep-2026): estas dos frases decían «e.g. tiktok today», y TikTok SÍ
 * está en `MESSAGING_CAPABLE` desde hace tiempo — se validó de punta a punta el 17-sep
 * (mensaje entrante ruteado y respuesta enviada). Hoy NINGÚN canal de la lista cae por
 * este atajo, así que el ejemplo no describía un caso real sino uno ya caducado. Se deja
 * sin ejemplo a propósito: el que se escriba mañana vuelve a envejecer igual.
 *
 * SCOPE: only for channelAccounts-model channels (whatsapp/instagram/messenger/tiktok).
 * gmail/calendar do NOT use this helper — they keep reading their own integration docs.
 *
 * @param {string[]} canales - channels to evaluate.
 * @returns {Object<string, (boolean|undefined)>} map canal -> operational. A value is
 *   `undefined` while its listener is still loading (surfaces should render NEUTRAL,
 *   not red), then true/false once resolved. Non-messaging-capable channels resolve to
 *   false on the first effect tick — no green/red flicker. (Ver la corrección de arriba:
 *   el ejemplo que había aquí, «tiktok today», llevaba tiempo siendo falso.)
 */
export function useChannelsOperational(canales) {
  const { context, loading: contextLoading } = useOrganization();
  const [operational, setOperational] = useState({});

  // Stable primitive dep so an inline-array argument doesn't re-fire the effect.
  const canalesKey = (canales || []).join(',');

  useEffect(() => {
    if (contextLoading) return;
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    // Owner path MUST match useDashboardData (prod-proven): org mode ONLY when
    // isOrganization AND orgId — otherwise personal clientes/{uid}. Gating on orgId
    // alone would query the wrong owner when orgId is present in personal mode.
    const basePath = context?.isOrganization && context?.orgId
      ? `organizations/${context.orgId}`
      : `clientes/${uid}`;
    const list = canalesKey ? canalesKey.split(',') : [];
    const unsubs = [];

    list.forEach((canal) => {
      // Condition (b): not messaging-capable -> operational=false, no listener.
      if (!MESSAGING_CAPABLE.includes(canal)) {
        setOperational(prev => ({ ...prev, [canal]: false }));
        return;
      }
      // Condition (a): ≥1 channelAccount canal==X estado=='conectado'.
      const q = query(
        collection(db, basePath, 'channelAccounts'),
        where('canal', '==', canal),
        where('estado', '==', 'conectado')
      );
      const unsub = onSnapshot(
        q,
        (snap) => setOperational(prev => ({ ...prev, [canal]: snap.size > 0 })),
        (error) => {
          console.error(`[useChannelsOperational] listener error (${canal}):`, error.code, error.message);
          setOperational(prev => ({ ...prev, [canal]: false }));
        }
      );
      unsubs.push(unsub);
    });

    return () => unsubs.forEach(fn => fn());
  }, [contextLoading, context?.isOrganization, context?.orgId, canalesKey]);

  return operational;
}

/**
 * Single-channel convenience over useChannelsOperational (same definition, one accessor).
 * @param {string} canal
 * @returns {boolean|undefined} true/false once resolved, `undefined` while loading
 *   (preserved as a third state — NOT collapsed to false — so badges show neutral).
 */
export function useChannelOperational(canal) {
  const map = useChannelsOperational([canal]);
  return map[canal];
}
